import { useMutation } from "@apollo/client";
import { useState } from "react";
import {
  CoachingAgendaItemNode,
  CoachingPhasedMeetingNode,
  CreateCoachingTopicMutationMutation,
  CreateCoachingTopicMutationMutationVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";

import createCoachingTopicMutation from "./graphql/create-coaching-topic-mutation";

const Tag = ({ tag }: { tag: string }) => {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
      <TopicflowIcon type="coachingDefault" className="h-3 w-3 text-gray-400" />
      {tag}
    </span>
  );
};

export const AgendaSection = ({
  nextMeeting,
  phasedPlan,
  nextMeetingId,
}: {
  nextMeeting: CoachingAgendaItemNode[];
  phasedPlan: CoachingPhasedMeetingNode[];
  nextMeetingId: number | null;
}) => {
  const label = useLabel();
  const [addingIndex, setAddingIndex] = useState<number | null>(null);

  const [createTopic] = useMutation<
    CreateCoachingTopicMutationMutation,
    CreateCoachingTopicMutationMutationVariables
  >(createCoachingTopicMutation);

  const handleAddToMeeting = async (
    meeting: CoachingPhasedMeetingNode,
    index: number,
  ) => {
    if (!nextMeetingId) return;
    const topics = (meeting.topics ?? []).filter((t): t is string => t != null);
    if (topics.length === 0) return;
    setAddingIndex(index);
    try {
      for (const title of topics) {
        await createTopic({ variables: { meetingId: nextMeetingId, title } });
      }
      successNotificationVar({
        title: "Topics added to next 1:1",
        timeout: 3000,
      });
    } finally {
      setAddingIndex(null);
    }
  };
  return (
    <div className="flex flex-col gap-8">
      {/* Next meeting */}
      <div className="rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 divide-y divide-gray-100">
        <div className="px-5 py-4">
          <h3 className="text-base font-semibold text-gray-900">
            Next {label("oneonone", { capitalize: true })} Agenda
          </h3>
        </div>
        <div className="px-5">
          <ul className="divide-y divide-gray-100">
            {nextMeeting.map((item, i) => (
              <li key={i} className="flex gap-4 py-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white text-sm font-semibold">
                  {i + 1}
                </div>
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">
                      {item.topic}
                    </span>
                    {(item.tags ?? [])
                      .filter((tag): tag is string => tag != null)
                      .map((tag) => (
                        <Tag key={tag} tag={tag} />
                      ))}
                  </div>
                  <p className="text-sm text-gray-500 italic leading-6">
                    {item.opener}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Phased plan */}
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-4">
          Phased Plan — Across 3 Meetings
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {phasedPlan.map((meeting, i) => (
            <div
              key={i}
              className="rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 p-5 flex flex-col gap-3"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white text-sm font-semibold">
                  {i + 1}
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {(meeting.meeting ?? "").replace(/^Meeting \d+ — /, "")}
                </span>
              </div>
              <p className="text-sm text-gray-500 leading-6">{meeting.theme}</p>
              <ul className="flex flex-col gap-2 mt-1">
                {(meeting.topics ?? []).map((topic) => (
                  <li
                    key={topic}
                    className="flex items-start gap-2.5 text-sm text-gray-700 leading-6"
                  >
                    <span className="mt-1.5 h-4 w-4 shrink-0 rounded border border-gray-300 bg-white" />
                    {topic}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-2">
                {nextMeetingId && (
                  <Button
                    theme={buttonTheme.primary}
                    size={ButtonSize.small}
                    disabled={addingIndex === i}
                    onClick={() => handleAddToMeeting(meeting, i)}
                  >
                    {addingIndex === i
                      ? "Adding…"
                      : `Add agenda to next ${label("oneonone", { capitalize: true })}`}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
